/**
 * Kislota-asos muvozanati: tuz gidrolizi va kuchsiz kislotalar.
 *
 * pH — zaryad balansi bo'yicha aniq yechim (Bronsted tizimlari):
 *   S + Σ Cⱼ·z̄ⱼ([H⁺]) + [H⁺] − Kw/[H⁺] = 0,
 *   S — kislota-asos xossasiga ega bo'lmagan ionlar zaryadi (Na⁺, Cl⁻, SO₄²⁻, NO₃⁻, ...),
 *   Cⱼ — j-tizimning umumiy konsentratsiyasi (masalan, CH₃COOH + CH₃COO⁻),
 *   z̄ⱼ — protonlanish ulushlari bo'yicha o'rtacha zaryad (Ka₁…Kaₙ).
 * Shuning uchun Na₂CO₃, CH₃COONa, Na₂S, sovun ishqoriy; NH₄Cl, FeCl₃, AlCl₃, CuSO₄ kislotali;
 * NaHCO₃ ≈ 8,3; CH₃COONH₄ ≈ 7 — gidroliz o'z-o'zidan chiqadi.
 *
 * Kuchsiz kislotalar reaksiyalari (proton almashinuvi): donor kislota Ka si aktseptorning qo'shma
 * kislotasi Ka sidan ≥ 10 marta katta bo'lsa (K ≥ 10; gaz/cho'kma chiqib ketishi muvozanatni suradi), proton to'liq o'tadi (CH₃COOH + CO₃²⁻ → HCO₃⁻ → CO₂↑,
 * CH₃COOH + S²⁻ → H₂S, CH₃COOH + NH₃ → NH₄⁺ ...). Metallar, erimaydigan karbonat/gidroksid/oksidlar
 * bilan reaksiyalarda kuchsiz kislota protonlari ham ishlatiladi, tezlik esa haqiqiy [H⁺] ga bog'liq.
 *
 * MANBA: Lurye "Analitik kimyo ma'lumotnomasi" (Ka, gidroliz konstantalari; 25 °C, ion kuchi 0).
 * Harorat: Kw(T) — Harned–Owen, Ka(T) — Vant-Goff (ionlanish entalpiyalari: Goldberg, Kishore, Lennen, 2002;
 * metall kationlari gidrolizi uchun taxminiy 40–68 kJ/mol). Qizdirilganda neytral pH < 7, gidroliz kuchayadi.
 * Soddalashtirish: aktivlik koeffitsientlari = 1;
 * metall kationlari bir bosqichli kislota sifatida (Mⁿ⁺·aq ⇌ MOH⁽ⁿ⁻¹⁾⁺ + H⁺).
 */
import { IONS } from './ions';
import { add, type Vessel } from './vessel';

const R = 8.314;
const T0 = 298.15;

/**
 * Suvning ion ko'paytmasi haroratga bog'liq (Harned–Owen): pKw = 4470,99/T − 6,0875 + 0,01706·T.
 * 0 °C: 14,94; 20 °C: 14,17; 25 °C: 14,00; 60 °C: 13,02; 100 °C: 12,26.
 */
export function pKw(T: number): number {
  const t = Math.min(573, Math.max(273.15, T));
  // +0,0053 — 25 °C da aniq 14,000 bo'lishi uchun kalibrlash
  return 4470.99 / t - 6.0875 + 0.01706 * t + 0.0053;
}

/** Vant-Goff: K(T) = K(298) · exp(−ΔH/R · (1/T − 1/298,15)); ΔH — kJ/mol */
export function vantHoff(K298: number, dHkJ: number, T: number): number {
  return K298 * Math.exp((-dHkJ * 1000) / R * (1 / T - 1 / T0));
}

type Pool = 'i' | 'd';
interface AcidSystem {
  /** to'liq deprotonlangan shakl zaryadi */
  zBase: number;
  /** ionlanish entalpiyalari ΔH°, kJ/mol (Ka(T) uchun) */
  dH: number[];
  /** Ka₁ … Kaₙ (to'liq protonlangan shakldan boshlab) */
  Ka: number[];
  /** idishdagi shakllar: havza, id, proton soni (deprotonlangan shaklga nisbatan) */
  members: { pool: Pool; id: string; h: number }[];
}

const sys = (zBase: number, Ka: number[], dH: number[], ...members: [Pool, string, number][]): AcidSystem => ({
  zBase,
  Ka,
  dH,
  members: members.map(([pool, id, h]) => ({ pool, id, h })),
});

export const ACID_SYSTEMS: AcidSystem[] = [
  sys(-1, [1.8e-5], [-0.4], ['d', 'CH3COOH', 1], ['i', 'CH3COO', 0]),
  sys(-2, [4.3e-7, 4.8e-11], [9.15, 14.85], ['i', 'HCO3', 1], ['i', 'CO3', 0]),
  sys(-2, [1.4e-2, 6.3e-8], [-17.8, -3.6], ['d', 'SO2', 2], ['i', 'HSO3', 1], ['i', 'SO3', 0]),
  sys(-2, [9.1e-8, 1.2e-13], [22, 49], ['d', 'H2S', 2], ['i', 'HS', 1], ['i', 'S', 0]),
  sys(-3, [7.1e-3, 6.3e-8, 4.5e-13], [-7.95, 4.15, 14.7], ['d', 'H3PO4', 3], ['i', 'H2PO4', 2], ['i', 'PO4', 0]),
  sys(-1, [5.8e-10], [13.8], ['d', 'H3BO3', 1], ['i', 'H2BO3', 0]),
  sys(0, [5.6e-10], [52.2], ['i', 'NH4', 1], ['d', 'NH3', 0]),
  sys(-1, [3.0e-8], [13.8], ['i', 'ClO', 0]),
  sys(-1, [2.5e-9], [30], ['i', 'BrO', 0]),
  sys(-1, [2.3e-11], [30], ['i', 'IO', 0]),
  sys(-2, [1.6e-10, 1.6e-12], [25, 25], ['i', 'SiO3', 0]),
  sys(-1, [1.3e-5], [0], ['i', 'St', 0]),
  // Gidrolizlanuvchi kationlar: [M(H₂O)]ⁿ⁺ ⇌ [M(OH)]⁽ⁿ⁻¹⁾⁺ + H⁺
  sys(2, [6.3e-3], [43], ['i', 'Fe3', 1]),
  sys(2, [1.6e-4], [43], ['i', 'Cr3', 1]),
  sys(2, [1.0e-5], [47], ['i', 'Al', 1]),
  sys(1, [3.0e-8], [40], ['i', 'Cu', 1]),
  sys(1, [2.5e-8], [55], ['i', 'Pb', 1]),
  sys(1, [2.5e-9], [55], ['i', 'Zn', 1]),
  sys(1, [3.2e-10], [55], ['i', 'Fe2', 1]),
  sys(1, [1.0e-10], [50], ['i', 'Ni', 1]),
  sys(1, [2.5e-11], [60], ['i', 'Mn', 1]),
  sys(1, [3.9e-12], [68], ['i', 'Mg', 1]),
];

const SYSTEM_IONS = new Set(ACID_SYSTEMS.flatMap((s) => s.members.filter((m) => m.pool === 'i').map((m) => m.id)));

/** Protonlanish darajasi bo'yicha o'rtacha proton soni */
function meanProtons(Ka: number[], H: number): number {
  const n = Ka.length;
  // αₕ ∝ Hʰ · Π_{i=1}^{n-h} Kaᵢ  (h — proton soni)
  let sum = 0;
  let wsum = 0;
  for (let h = 0; h <= n; h++) {
    let t = Math.pow(H, h);
    for (let i = 0; i < n - h; i++) t *= Ka[i] as number;
    sum += t;
    wsum += h * t;
  }
  return sum > 0 ? wsum / sum : 0;
}

/** Muvozanat [H⁺], mol/L (eritma bo'lmasa — NaN) */
export function equilibriumH(v: Vessel): number {
  if (v.volumeMl <= 0) return NaN;
  const L = v.volumeMl / 1000;
  let S = 0;
  for (const [id, n] of Object.entries(v.ions)) {
    if (id === 'H' || id === 'OH' || SYSTEM_IONS.has(id)) continue;
    S += (IONS[id]?.charge ?? 0) * (n / L);
  }
  const T = v.T;
  const Kw = Math.pow(10, -pKw(T));
  const active: { C: number; zBase: number; Ka: number[] }[] = [];
  for (const s of ACID_SYSTEMS) {
    let C = 0;
    for (const m of s.members) C += (m.pool === 'i' ? v.ions[m.id] : v.dissolved[m.id]) ?? 0;
    if (C > 1e-15) active.push({ C: C / L, zBase: s.zBase, Ka: Math.abs(T - T0) < 0.01 ? s.Ka : s.Ka.map((k, i) => vantHoff(k, s.dH[i] ?? 0, T)) });
  }
  const f = (H: number) => {
    let q = S + H - Kw / H;
    for (const a of active) q += a.C * (a.zBase + meanProtons(a.Ka, H));
    return q;
  };
  // f(H) — H bo'yicha o'suvchi: log₁₀H ∈ [−15.5; 1.5] da ikkiga bo'lish
  let lo = -15.5;
  let hi = 1.5;
  for (let i = 0; i < 70; i++) {
    const mid = (lo + hi) / 2;
    if (f(Math.pow(10, mid)) > 0) hi = mid;
    else lo = mid;
  }
  return Math.pow(10, (lo + hi) / 2);
}

export function solvePH(v: Vessel): number {
  const H = equilibriumH(v);
  if (!Number.isFinite(H)) return NaN;
  return Math.min(14, Math.max(0, -Math.log10(H)));
}

// ── Kuchsiz kislotalar: proton donorlari ───────────────────
interface Donor {
  id: string;
  Ka: number;
  /** qo'shma asos (ion) */
  conj: string;
  pool: Pool;
}
/** Molekulyar va ion holidagi kuchsiz kislotalar (kuchlisi avval) */
const DONORS: Donor[] = [
  { id: 'SO2', Ka: 1.4e-2, conj: 'HSO3', pool: 'd' },
  { id: 'H3PO4', Ka: 7.1e-3, conj: 'H2PO4', pool: 'd' },
  { id: 'CH3COOH', Ka: 1.8e-5, conj: 'CH3COO', pool: 'd' },
  { id: 'H2S', Ka: 9.1e-8, conj: 'HS', pool: 'd' },
  { id: 'HSO3', Ka: 6.3e-8, conj: 'SO3', pool: 'i' },
];

/** Kuchsiz kislota bilan reaksiya uchun minimal Ka (metallar, erimaydigan tuzlar) */
export const DONOR_MIN_KA = 1e-6;

function donorPool(v: Vessel, d: Donor): Record<string, number> {
  return d.pool === 'i' ? v.ions : v.dissolved;
}

/** Erkin H⁺ + kuchsiz kislotalar beradigan protonlar (Ka ≥ minKa), mol */
export function availableProtons(v: Vessel, minKa = DONOR_MIN_KA): number {
  let n = v.ions.H ?? 0;
  for (const d of DONORS) if (d.Ka >= minKa) n += donorPool(v, d)[d.id] ?? 0;
  return n;
}

/** Donor kislotalar umumiy konsentratsiyasi, mol/L */
export function weakAcidConc(v: Vessel, minKa = DONOR_MIN_KA): number {
  if (v.volumeMl <= 0) return 0;
  let n = 0;
  for (const d of DONORS) if (d.Ka >= minKa) n += donorPool(v, d)[d.id] ?? 0;
  return (n / v.volumeMl) * 1000;
}

/**
 * Sirt reaksiyalari (metall, erimaydigan tuz) uchun samarali [H⁺]: kuchsiz kislotada dissotsiatsiyalanmagan
 * molekulalar ham sirtda proton beradi — √([H⁺]·C) (empirik; 1 M CH₃COOH ≈ HCl dan 15 marta sekin).
 */
export function surfaceH(v: Vessel): number {
  const free = v.volumeMl > 0 ? ((v.ions.H ?? 0) / v.volumeMl) * 1000 : 0;
  if (!hasWeakAcid(v)) return free;
  const eq = equilibriumH(v);
  return Math.max(free, Math.sqrt(eq * weakAcidConc(v)));
}

export function hasWeakAcid(v: Vessel, minKa = DONOR_MIN_KA): boolean {
  return DONORS.some((d) => d.Ka >= minKa && (donorPool(v, d)[d.id] ?? 0) > 1e-12);
}

/** n mol protonni olish: avval erkin H⁺, keyin kuchsiz kislotalar (HA → A⁻) */
export function takeProtons(v: Vessel, n: number, minKa = DONOR_MIN_KA): number {
  let left = n;
  const h = Math.min(left, v.ions.H ?? 0);
  if (h > 0) {
    add(v.ions, 'H', -h);
    left -= h;
  }
  for (const d of DONORS) {
    if (left <= 1e-18) break;
    if (d.Ka < minKa) continue;
    const pool = donorPool(v, d);
    const x = Math.min(left, pool[d.id] ?? 0);
    if (x <= 0) continue;
    add(pool, d.id, -x);
    add(v.ions, d.conj, x);
    left -= x;
  }
  return n - left;
}

/** Proton aktseptorlari: qo'shma kislotaning Ka si va protonlangandan keyingi shakl */
interface Acceptor {
  pool: Pool;
  id: string;
  /** qo'shma kislota Ka si (qancha kichik — asos shuncha kuchli) */
  Ka: number;
  /** protonlangan mahsulot */
  to: { pool: 'i' | 'd' | 's' | 'g'; id: string };
  /** bir birlikka kerakli protonlar */
  protons: number;
}
const ACCEPTORS: Acceptor[] = [
  { pool: 'i', id: 'S', Ka: 1.2e-13, to: { pool: 'i', id: 'HS' }, protons: 1 },
  { pool: 'i', id: 'CO3', Ka: 4.8e-11, to: { pool: 'i', id: 'HCO3' }, protons: 1 },
  { pool: 'i', id: 'SiO3', Ka: 1.6e-10, to: { pool: 's', id: 'H2SiO3' }, protons: 2 },
  { pool: 'd', id: 'NH3', Ka: 5.6e-10, to: { pool: 'i', id: 'NH4' }, protons: 1 },
  { pool: 'i', id: 'PO4', Ka: 6.3e-8, to: { pool: 'i', id: 'H2PO4' }, protons: 2 },
  { pool: 'i', id: 'SO3', Ka: 6.3e-8, to: { pool: 'i', id: 'HSO3' }, protons: 1 },
  { pool: 'i', id: 'HS', Ka: 9.1e-8, to: { pool: 'd', id: 'H2S' }, protons: 1 },
  { pool: 'i', id: 'HCO3', Ka: 4.3e-7, to: { pool: 'g', id: 'CO2' }, protons: 1 },
];

/**
 * Kuchsiz kislotalardan asoslarga proton o'tishi (oniy, to'liq). Qaytaradi: ajralgan CO₂, mol.
 * Erkin H⁺ bilan reaksiyalar (kuchli kislotalar) reactions.ts dagi umumiy qoidalarda.
 */
export function weakAcidTransfers(v: Vessel, evolve: (gas: string, n: number) => void): void {
  if (v.volumeMl <= 0) return;
  for (const a of ACCEPTORS) {
    for (const d of DONORS) {
      if (d.Ka < 10 * a.Ka) continue;
      const acc = (a.pool === 'i' ? v.ions : v.dissolved)[a.id] ?? 0;
      const don = donorPool(v, d)[d.id] ?? 0;
      if (acc <= 1e-15 || don <= 1e-15) continue;
      // o'z qo'shma juftiga proton bermaydi (HSO₃⁻ → SO₃²⁻ ni o'zi qaytaradi)
      if (d.conj === a.id || d.id === a.to.id) continue;
      const x = Math.min(acc, don / a.protons);
      add(a.pool === 'i' ? v.ions : v.dissolved, a.id, -x);
      add(donorPool(v, d), d.id, -x * a.protons);
      add(v.ions, d.conj, x * a.protons);
      if (a.to.pool === 'g') evolve(a.to.id, x);
      else add(a.to.pool === 'i' ? v.ions : a.to.pool === 'd' ? v.dissolved : v.solids, a.to.id, x);
    }
  }
}
